import React, { useEffect, useRef, useState } from 'react';
import { fmtHM } from '../lib/format.js';
import { Icon } from './Icon.jsx';

const SEGMENT_PALETTE = ['#05E6C5', '#79F1E0', '#00C1C1', '#B2F7ED', '#58EED8'];

export function ProgressMeter({ entries, target, userFirstName, exempt = false }) {
  const total = entries.reduce((s, e) => s + e.hours * 60 + e.minutes, 0);
  const done = total === target;
  const over = total > target;
  const under = total < target;
  const remaining = Math.max(0, target - total);
  const overtime = Math.max(0, total - target);

  const containerRef = useRef(null);
  const [confettiKey, setConfettiKey] = useState(0);
  const lastDone = useRef(false);
  useEffect(() => {
    // v2.6 — no "hit your 8h" celebration for excluded users; there's no
    // target to celebrate against.
    if (!exempt && done && !lastDone.current) setConfettiKey((k) => k + 1);
    lastDone.current = done;
  }, [done, exempt]);

  let statusEl;
  if (exempt) {
    // v2.6 — neutral, judgement-free status for an excluded user. No
    // "remaining" / "overtime" nag — logging is optional for them.
    statusEl = (
      <span
        style={{
          color: '#007C7C',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontWeight: 600,
        }}
      >
        <Icon name="clock" size={14} stroke={2} /> Logging optional
      </span>
    );
  } else if (under) {
    statusEl = (
      <span
        style={{
          color: '#B57651',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#FFA672',
          }}
        />
        {fmtHM(remaining)} remaining
      </span>
    );
  } else if (done) {
    statusEl = (
      <span
        style={{
          color: '#026153',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontWeight: 600,
        }}
      >
        <Icon name="check" size={14} stroke={2.4} /> Eight hours, clean. Nice
        work{userFirstName ? `, ${userFirstName}` : ''}.
      </span>
    );
  } else {
    statusEl = (
      <span
        style={{
          color: 'var(--sp-fg-1)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontWeight: 600,
        }}
      >
        <Icon name="flame" size={14} stroke={2} />
        <span
          style={{
            textDecoration: 'underline',
            textDecorationThickness: 2,
            textUnderlineOffset: 3,
          }}
        >
          +{fmtHM(overtime)} overtime
        </span>
      </span>
    );
  }

  // Segmented meter — alternating mint hues, apricot for the overtime portion
  const renderSegments = () => {
    const trackTotalMin = over ? total : target;
    let cumulative = 0;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 3,
          height: 18,
          position: 'relative',
        }}
      >
        {entries.map((e, i) => {
          const mins = e.hours * 60 + e.minutes;
          const widthPct = trackTotalMin
            ? (mins / trackTotalMin) * 100
            : 0;
          const before = cumulative;
          cumulative += mins;
          const isOvertime = over && before >= target;
          const splitsTarget =
            over && before < target && cumulative > target;
          const palette = SEGMENT_PALETTE[i % SEGMENT_PALETTE.length];
          let background = isOvertime ? '#FFA672' : palette;
          if (splitsTarget) {
            const splitPct = ((target - before) / mins) * 100;
            background = `linear-gradient(to right, ${palette} ${splitPct}%, #FFA672 0)`;
          }
          return (
            <div
              key={e.id}
              title={`${e.activityName} · ${fmtHM(mins)}`}
              style={{
                width: `${widthPct}%`,
                background,
                borderRadius: 4,
                transition: 'width .25s ease',
              }}
            />
          );
        })}
        {under && (
          <div
            style={{
              flex: 1,
              background:
                'repeating-linear-gradient(135deg, #F2F2F2 0 6px, transparent 6px 12px)',
              borderRadius: 4,
              border: '1px dashed var(--sp-border)',
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{
        background: '#fff',
        border: '1px solid var(--sp-border)',
        borderRadius: 18,
        padding: '24px 28px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: 'var(--sp-shadow-xs)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontSize: 44,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmtHM(total) || '0m'}
        </div>
        <div
          style={{ fontSize: 14, color: 'var(--sp-fg-3)', fontWeight: 500 }}
        >
          {exempt ? 'logged today' : `of ${fmtHM(target)} daily target`}
        </div>
        <div style={{ marginLeft: 'auto' }}>{statusEl}</div>
      </div>

      <div style={{ marginTop: 18 }}>{renderSegments()}</div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          marginTop: 8,
          fontSize: 10,
          color: 'var(--sp-fg-4)',
          fontWeight: 600,
          letterSpacing: '0.04em',
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
          <div key={n} style={{ textAlign: 'right', paddingRight: 2 }}>
            {n}h
          </div>
        ))}
      </div>

      {done && !exempt && (
        <div className="tt-confetti" key={confettiKey}>
          {Array.from({ length: 24 }).map((_, i) => {
            const colors = [
              '#05E6C5',
              '#79F1E0',
              '#FFA672',
              '#FFCBA8',
              '#00C1C1',
            ];
            const c = colors[i % colors.length];
            const dx = (Math.random() - 0.5) * 200;
            const left = 6 + Math.random() * 88;
            const delay = Math.random() * 0.4;
            const rot = (Math.random() * 720 - 360) | 0;
            return (
              <span
                key={i}
                style={{
                  left: `${left}%`,
                  background: c,
                  '--dx': `${dx}px`,
                  '--rot': `${rot}deg`,
                  animationDelay: `${delay}s`,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
